import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import './SnakeGame.css';

const GRID = 20;
const CELL = 20;
const CANVAS_SIZE = GRID * CELL;
const INITIAL_SPEED = 150;
const MIN_SPEED = 80;

const DIR = { UP: [0, -1], DOWN: [0, 1], LEFT: [-1, 0], RIGHT: [1, 0] };
const OPPOSITE = { UP: 'DOWN', DOWN: 'UP', LEFT: 'RIGHT', RIGHT: 'LEFT' };

function randomApples(snake, count = 3) {
    const occ = new Set(snake.map(s => `${s[0]},${s[1]}`));
    const free = [];
    for (let x = 0; x < GRID; x++)
        for (let y = 0; y < GRID; y++)
            if (!occ.has(`${x},${y}`)) free.push([x, y]);
    const result = [];
    const limit = Math.min(count, free.length);
    for (let i = 0; i < limit; i++) {
        const idx = Math.floor(Math.random() * free.length);
        result.push(free[idx]);
        free.splice(idx, 1);
    }
    return result;
}

export default function SnakeGame({
    onGameOver,
    isGenerating,
    generationSteps = [],
    effectiveStatusIndex = 0,
    onViewResults,
    articleReady = false,
    articleFailed = false,
    statusLog = [],
}) {
    const { user } = useAuth();
    const canvasRef = useRef(null);
    const logEndRef = useRef(null);

    const [started, setStarted] = useState(false);
    const [dead, setDead] = useState(false);
    const [score, setScore] = useState(0);
    const [best, setBest] = useState(0);
    const [leaderboard, setLeaderboard] = useState([]);

    const stateRef = useRef({
        snake: [[10, 10], [9, 10], [8, 10]],
        apples: randomApples([[10, 10], [9, 10], [8, 10]], 3),
        dir: 'RIGHT',
        nextDir: 'RIGHT',
        score: 0,
        speed: INITIAL_SPEED,
        dead: false,
    });

    const fetchLB = useCallback(async () => {
        try {
            const res = await api.get('/api/snake/leaderboard');
            setLeaderboard(res.data?.leaderboard || []);
        } catch {}
    }, []);

    useEffect(() => { fetchLB(); }, [fetchLB]);

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const c = CELL;
        const s = stateRef.current;

        ctx.fillStyle = '#0a0a0f';
        ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

        ctx.strokeStyle = 'rgba(99,102,241,0.12)';
        ctx.lineWidth = 0.5;
        for (let i = 0; i <= GRID; i++) {
            ctx.beginPath(); ctx.moveTo(i * c, 0); ctx.lineTo(i * c, CANVAS_SIZE); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(0, i * c); ctx.lineTo(CANVAS_SIZE, i * c); ctx.stroke();
        }

        ctx.strokeStyle = '#6366f1';
        ctx.lineWidth = 2;
        ctx.strokeRect(1, 1, CANVAS_SIZE - 2, CANVAS_SIZE - 2);

        ctx.fillStyle = '#ef4444';
        ctx.shadowColor = '#ef4444';
        ctx.shadowBlur = 8;
        for (const apple of s.apples) {
            ctx.beginPath();
            ctx.arc(apple[0] * c + c / 2, apple[1] * c + c / 2, c * 0.4, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.shadowBlur = 0;

        s.snake.forEach((seg, i) => {
            const a = 1 - (i / s.snake.length) * 0.5;
            ctx.fillStyle = i === 0 ? `rgba(52,211,153,${a})` : `rgba(16,185,129,${a})`;
            ctx.shadowColor = '#10b981';
            ctx.shadowBlur = i === 0 ? 10 : 4;
            ctx.fillRect(seg[0] * c + 1, seg[1] * c + 1, c - 2, c - 2);
        });
        ctx.shadowBlur = 0;
    }, []);

    const reset = useCallback(() => {
        const snake = [[10, 10], [9, 10], [8, 10]];
        stateRef.current = {
            snake,
            apples: randomApples(snake, 3),
            dir: 'RIGHT',
            nextDir: 'RIGHT',
            score: 0,
            speed: INITIAL_SPEED,
            dead: false,
        };
        setScore(0);
        setDead(false);
        draw();
    }, [draw]);

    useEffect(() => {
        if (!started) { draw(); return; }
        const loop = setInterval(() => {
            const s = stateRef.current;
            if (s.dead) return;

            s.dir = s.nextDir;
            const [dx, dy] = DIR[s.dir];
            const head = [s.snake[0][0] + dx, s.snake[0][1] + dy];

            if (head[0] < 0 || head[0] >= GRID || head[1] < 0 || head[1] >= GRID ||
                s.snake.some(seg => seg[0] === head[0] && seg[1] === head[1])) {
                s.dead = true;
                setDead(true);
                if (s.score > 0) {
                    setBest(prev => Math.max(prev, s.score));
                    if (onGameOver) onGameOver(s.score);
                }
                fetchLB();
                draw();
                return;
            }

            s.snake = [head, ...s.snake];
            const eatenIdx = s.apples.findIndex(a => a[0] === head[0] && a[1] === head[1]);
            if (eatenIdx !== -1) {
                s.score += 1;
                setScore(s.score);
                const replacement = randomApples(s.snake, 1);
                if (replacement.length > 0) {
                    s.apples[eatenIdx] = replacement[0];
                } else {
                    s.apples.splice(eatenIdx, 1);
                }
                s.speed = Math.max(MIN_SPEED, s.speed - 2);
            } else {
                s.snake.pop();
            }
            draw();
        }, INITIAL_SPEED);

        draw();

        return () => clearInterval(loop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dead, started]);

    useEffect(() => {
        const handler = (e) => {
            const map = {
                ArrowUp: 'UP', ArrowDown: 'DOWN', ArrowLeft: 'LEFT', ArrowRight: 'RIGHT',
                w: 'UP', W: 'UP', s: 'DOWN', S: 'DOWN', a: 'LEFT', A: 'LEFT', d: 'RIGHT', D: 'RIGHT',
            };
            if (e.key === ' ' && stateRef.current.dead) {
                e.preventDefault();
                reset();
                setStarted(true);
                return;
            }
            const dir = map[e.key];
            if (dir && !started) setStarted(true);
            if (dir && !stateRef.current.dead) {
                e.preventDefault();
                if (dir !== OPPOSITE[stateRef.current.dir]) {
                    stateRef.current.nextDir = dir;
                }
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [reset]);

    useEffect(() => {
        if (logEndRef.current) logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }, [statusLog.length]);

    const visibleSteps = generationSteps.filter(s => s.key !== 'queued' && s.key !== 'failed');
    const currentUsername = user?.name || '';

    return (
        <div className="snake-game-container">
            <div className={`snake-status-banner ${articleReady ? 'article-ready' : ''} ${articleFailed ? 'article-failed' : ''}`}
                 onClick={(articleReady || articleFailed) ? onViewResults : undefined}
                 role={(articleReady || articleFailed) ? 'button' : undefined}
                 tabIndex={(articleReady || articleFailed) ? 0 : undefined}>
                {articleReady ? (
                    <span className="snake-banner-ready">Article ready — click to view results</span>
                ) : articleFailed ? (
                    <span className="snake-banner-failed">Generation failed — click to view details</span>
                ) : (
                    <div className="snake-banner-steps">
                        {visibleSteps.map((step) => {
                            const realIdx = generationSteps.findIndex(s => s.key === step.key);
                            const isPast = effectiveStatusIndex > realIdx;
                            const isActive = effectiveStatusIndex === realIdx;
                            return (
                                <span key={step.key} className={`snake-step-chip ${isPast ? 'past' : ''} ${isActive ? 'active' : ''}`}>
                                    {isPast ? '\u2713 ' : ''}{step.label}
                                </span>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="snake-main">
                <div className="snake-canvas-wrapper" onClick={() => { if (!started) setStarted(true); }}>
                    <canvas ref={canvasRef} width={CANVAS_SIZE} height={CANVAS_SIZE} />
                    {!started && !dead && (
                        <div className="snake-game-over-overlay" style={{ cursor: 'pointer' }}>
                            <div className="snake-game-over-content">
                                <p style={{ fontSize: '1.2rem', color: '#34d399' }}>Click or press any arrow key to play</p>
                            </div>
                        </div>
                    )}
                    {dead && (
                        <div className="snake-game-over-overlay">
                            <div className="snake-game-over-content">
                                <h2>Game Over</h2>
                                <p className="snake-final-score">Score: {score}</p>
                                {score > 0 && score >= best && <p className="snake-new-best">New personal best!</p>}
                                <p className="snake-restart-hint">Press Space to restart</p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="snake-side-panel">
                    <div className="snake-score-display">
                        <span className="snake-score-label">SCORE</span>
                        <span className="snake-score-value">{score}</span>
                    </div>

                    <div className="snake-live-log">
                        <h4 className="snake-panel-title">Generation Log</h4>
                        <div className="snake-log-entries">
                            {statusLog.map((entry, i) => (
                                <div key={i} className="snake-live-log-entry">
                                    <span className="snake-log-time">
                                        {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                    </span>
                                    <span className="snake-log-msg">{entry.message}</span>
                                </div>
                            ))}
                            <div ref={logEndRef} />
                        </div>
                    </div>

                    <div className="snake-leaderboard">
                        <h4 className="snake-panel-title">Leaderboard</h4>
                        {leaderboard.length > 0 ? (
                            <table className="snake-lb-table">
                                <thead><tr><th>#</th><th>Player</th><th>Score</th></tr></thead>
                                <tbody>
                                    {leaderboard.map((row, i) => {
                                        const isMe = currentUsername && row.username === currentUsername;
                                        return (
                                            <tr key={i} className={`${row.rank <= 3 ? 'snake-lb-top' : ''} ${isMe ? 'snake-lb-me' : ''}`}>
                                                <td>{row.rank}</td>
                                                <td>{row.username}{isMe ? ' (you)' : ''}</td>
                                                <td>{row.score}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <p className="snake-lb-empty">No scores yet</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
